import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useRef } from 'react';
import { Plus, BookUp } from 'lucide-react';
import { Button } from '@edusense/shared-ui-kit';
import { DraggableSection } from './DraggableSection';
import * as S from './CurriculumTabContent.styles';
/**
 * 커리큘럼 패널 컴포넌트
 * 
 * 왼쪽에 위치한 커리큘럼 관리 패널
 * 섹션 추가, 커리큘럼 임포트, 섹션 목록 표시 기능
 */ export const CurriculumPanel = ({ sections, isFullWidth, isAddingSection, newSectionTitleField, editingSectionId, editingSectionTitleCache, addingLessonToSectionId, newLessonTitleField, curriculumMachineSelectedLesson, editingLessonSectionId, editingLessonId, editingLessonTitleCache, onToggleAddSectionForm, onSubmitAddSection, onSetNewSectionTitle, onCurriculumMarkdownImport, onStartEditSection, onToggleAddLessonForm, onDeleteSection, onSubmitEditSection, onSubmitAddLesson, onSetEditingSectionTitle, onSetNewLessonTitle, onLessonSelect, onStartEditLesson, onDeleteLesson, onSubmitEditLesson, onSetEditingLessonTitle, onMoveLesson, onTogglePreview, onCurriculumMarkdownDrop, onCurriculumDropStateChange, isDragActive, send })=>{
    const curriculumFileInputRef = useRef(null);
    const dragCounterRef = useRef(0);
    const containsFile = (event)=>event.dataTransfer?.types?.includes('Files');
    const handleDragEnter = (event)=>{
        if (!containsFile(event)) return;
        event.preventDefault();
        dragCounterRef.current += 1;
        onCurriculumDropStateChange(true);
    };
    const handleDragOver = (event)=>{
        if (!containsFile(event)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
    };
    const handleDragLeave = (event)=>{
        if (!containsFile(event)) return;
        dragCounterRef.current = Math.max(dragCounterRef.current - 1, 0);
        if (dragCounterRef.current === 0) {
            onCurriculumDropStateChange(false);
        }
    };
    const handleDrop = (event)=>{
        if (!containsFile(event)) return;
        event.preventDefault();
        dragCounterRef.current = 0;
        onCurriculumDropStateChange(false);
        onCurriculumMarkdownDrop(event.dataTransfer?.files || null);
    };
    // 커리큘럼 임포트 버튼 클릭 핸들러
    const handleImportClick = ()=>{
        curriculumFileInputRef.current?.click();
    };
    // 새 섹션 제목 변경 핸들러
    const handleNewSectionTitleChange = (e)=>{
        onSetNewSectionTitle(e.target.value);
    };
    // 섹션 추가 취소 핸들러
    const handleCancelAddSection = ()=>{
        send({
            type: 'TOGGLE_ADD_SECTION_FORM'
        });
    };
    return /*#__PURE__*/ _jsxs(S.CurriculumPanel, {
        $isHidden: isFullWidth,
        $isDragActive: isDragActive,
        onDragEnter: handleDragEnter,
        onDragOver: handleDragOver,
        onDragLeave: handleDragLeave,
        onDrop: handleDrop,
        children: [
            isDragActive && /*#__PURE__*/ _jsx(S.DropOverlay, {
                children: "마크다운 파일을 놓으면 커리큘럼을 임포트합니다"
            }),
            /*#__PURE__*/ _jsxs("div", {
                style: {
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px'
                },
                children: [
                    /*#__PURE__*/ _jsx("h2", {
                        style: {
                            margin: 0,
                            fontSize: '1.25rem'
                        },
                        children: "커리큘럼"
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        style: {
                            display: 'flex',
                            gap: '8px'
                        },
                        children: [
                            /*#__PURE__*/ _jsxs(Button, {
                                variant: "outline",
                                size: "sm",
                                onClick: handleImportClick,
                                title: "마크다운 파일에서 전체 커리큘럼 가져오기",
                                children: [
                                    /*#__PURE__*/ _jsx(BookUp, {
                                        size: 16
                                    }),
                                    " 코스 임포트"
                                ]
                            }),
                            /*#__PURE__*/ _jsx(S.HiddenFileInput, {
                                type: "file",
                                ref: curriculumFileInputRef,
                                onChange: onCurriculumMarkdownImport,
                                accept: ".md,text/markdown",
                                style: {
                                    display: 'none'
                                }
                            }),
                            /*#__PURE__*/ _jsxs(Button, {
                                variant: "outline",
                                size: "sm",
                                onClick: onToggleAddSectionForm,
                                children: [
                                    /*#__PURE__*/ _jsx(Plus, {
                                        size: 16
                                    }),
                                    " 섹션 추가"
                                ]
                            })
                        ]
                    })
                ]
            }),
            isAddingSection && /*#__PURE__*/ _jsxs(S.AddItemForm, {
                onSubmit: onSubmitAddSection,
                children: [
                    /*#__PURE__*/ _jsx(S.InputField, {
                        value: newSectionTitleField,
                        onChange: handleNewSectionTitleChange,
                        placeholder: "새 섹션 제목",
                        autoFocus: true
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        type: "submit",
                        variant: "primary",
                        size: "sm",
                        children: "추가"
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        type: "button",
                        variant: "outline",
                        size: "sm",
                        onClick: handleCancelAddSection,
                        children: "취소"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(S.SectionList, {
                children: sections.map((section, index)=>/*#__PURE__*/ _jsx(DraggableSection, {
                        section: section,
                        index: index,
                        editingSectionId: editingSectionId,
                        editingSectionTitleCache: editingSectionTitleCache,
                        addingLessonToSectionId: addingLessonToSectionId,
                        newLessonTitleField: newLessonTitleField,
                        curriculumMachineSelectedLesson: curriculumMachineSelectedLesson,
                        editingLessonSectionId: editingLessonSectionId,
                        editingLessonId: editingLessonId,
                        editingLessonTitleCache: editingLessonTitleCache,
                        onStartEditSection: onStartEditSection,
                        onToggleAddLessonForm: onToggleAddLessonForm,
                        onDeleteSection: onDeleteSection,
                        onSubmitEditSection: onSubmitEditSection,
                        onSubmitAddLesson: onSubmitAddLesson,
                        onSetEditingSectionTitle: onSetEditingSectionTitle,
                        onSetNewLessonTitle: onSetNewLessonTitle,
                        onLessonSelect: onLessonSelect,
                        onStartEditLesson: onStartEditLesson,
                        onDeleteLesson: onDeleteLesson,
                        onSubmitEditLesson: onSubmitEditLesson,
                        onSetEditingLessonTitle: onSetEditingLessonTitle,
                        onMoveLesson: onMoveLesson,
                        onTogglePreview: onTogglePreview,
                        send: send
                    }, section.id))
            })
        ]
    });
};
